import { useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineReload } from 'react-icons/ai';
import { Link } from 'react-router-dom';
import { toasting } from 'src/lib/components/Toast.component';
import { fget, fpost } from 'src/lib/fetch';
import { DeviceDetailPanel } from 'src/modules/map-dialog/components/DialogWaterLevel.component';
import { MapDevicePanel } from 'src/redux/slices/mapDevicePanels.slice';
import './DeviceMonitor.css';

type DeviceSortOrder =
  | 'area-name'
  | 'name-asc'
  | 'name-desc'
  | 'updated-desc'
  | 'updated-asc'
  | 'active-first'
  | 'manual';

const MANUAL_ORDER_STORAGE_KEY = 'device-monitor-manual-order';

const getPublishTimestamp = (value: string) => {
  const raw = value.trim();
  if (!raw) return 0;
  if (/^\d+$/.test(raw)) {
    const timestamp = Number(raw);
    return raw.length <= 10 ? timestamp * 1000 : timestamp;
  }
  const timestamp = new Date(raw.replace(/\//g, '-').replace(' ', 'T')).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isDeviceActive = (device: MapDevicePanel) =>
  Date.now() - getPublishTimestamp(device.publish) < 30 * 60 * 1000;

const getLatestDeviceRecord = async (deviceId: string) => {
  const response = await fpost<any>('api/device/record/query', {
    body: {
      query: {
        ExpressionAttributeValues: {
          ':id': { S: deviceId },
        },
        KeyConditionExpression: 'DEVICE_ID = :id',
        TableName: 'DEVICE_RECORD',
        ScanIndexForward: false,
        Limit: 1,
      },
    },
  });
  return response?.[1]?.Items?.[0];
};

export function DeviceMonitorPage() {
  const [devices, setDevices] = useState<MapDevicePanel[]>([]);
  const [searchText, setSearchText] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<DeviceSortOrder>('area-name');
  const [manualOrder, setManualOrder] = useState<string[]>(() => {
    try {
      const saved = window.localStorage.getItem(MANUAL_ORDER_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [draggingDeviceId, setDraggingDeviceId] = useState('');
  const lastDragTargetRef = useRef('');
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showKpi, setShowKpi] = useState(true);
  const fetchingRef = useRef(false);

  const loadDevices = async (background = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!background) setIsLoading(true);

    try {
      const response = await fget('api/devices');
      const items = response?.[1]?.Items || [];
      const nextDevices = await Promise.all(
        items.map(async (item: any) => {
          const deviceId = item.DEVICE_ID?.S || '';
          if (!deviceId) return null;

          let record: any;
          try {
            record = await getLatestDeviceRecord(deviceId);
          } catch (error) {
            console.log(error);
          }

          return {
            deviceId,
            pos: item.DEVICE_NAME?.S || deviceId,
            waterLevel: record?.WATER_LEVEL?.S || '0',
            flowRate: record?.FLOW_RATE?.S || '0',
            velocity: record?.VELOCITY?.S || '0',
            publish:
              record?.Publish?.S ||
              record?.PUBLISH?.S ||
              record?.PUBLISH_FORMATED?.S ||
              '',
            areaName: item.AREA_NAME?.S || '',
            riverName: item.RIVER_NAME?.S || '',
          } as MapDevicePanel;
        }),
      );

      const sortedDevices = nextDevices
        .filter((device): device is MapDevicePanel => device !== null)
        .sort((left, right) =>
          `${left.areaName}\u0000${left.pos}`.localeCompare(`${right.areaName}\u0000${right.pos}`, 'ja'),
        );
      setDevices(sortedDevices);
      setManualOrder((current) => {
        const deviceIds = sortedDevices.map((device) => device.deviceId);
        return [
          ...current.filter((deviceId) => deviceIds.includes(deviceId)),
          ...deviceIds.filter((deviceId) => !current.includes(deviceId)),
        ];
      });
    } catch (error) {
      console.log(error);
      toasting({
        children: 'デバイスモニターのデータ取得に失敗しました。',
        containerProps: { className: 'border-red-600' },
      });
    } finally {
      fetchingRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadDevices();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = window.setInterval(() => void loadDevices(true), 30000);
    return () => window.clearInterval(intervalId);
  }, [autoRefresh]);

  useEffect(() => {
    window.localStorage.setItem(MANUAL_ORDER_STORAGE_KEY, JSON.stringify(manualOrder));
  }, [manualOrder]);

  const areas = useMemo(
    () => Array.from(new Set(devices.map((device) => device.areaName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ja')),
    [devices],
  );

  const visibleDevices = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase();
    return devices
      .filter((device) => {
        if (areaFilter && device.areaName !== areaFilter) return false;
        if (!query) return true;
        return [device.pos, device.deviceId, device.areaName, device.riverName]
          .some((value) => value.toLocaleLowerCase().includes(query));
      })
      .sort((left, right) => {
        const nameComparison = left.pos.localeCompare(right.pos, 'ja');
        if (sortOrder === 'name-asc') return nameComparison;
        if (sortOrder === 'name-desc') return -nameComparison;
        if (sortOrder === 'updated-desc') {
          return getPublishTimestamp(right.publish) - getPublishTimestamp(left.publish) || nameComparison;
        }
        if (sortOrder === 'updated-asc') {
          return getPublishTimestamp(left.publish) - getPublishTimestamp(right.publish) || nameComparison;
        }
        if (sortOrder === 'active-first') {
          return Number(isDeviceActive(right)) - Number(isDeviceActive(left)) || nameComparison;
        }
        if (sortOrder === 'manual') {
          const leftIndex = manualOrder.indexOf(left.deviceId);
          const rightIndex = manualOrder.indexOf(right.deviceId);
          return leftIndex - rightIndex;
        }
        return left.areaName.localeCompare(right.areaName, 'ja') || nameComparison;
      });
  }, [areaFilter, devices, manualOrder, searchText, sortOrder]);

  const selectSortOrder = (nextSortOrder: DeviceSortOrder) => {
    if (nextSortOrder === 'manual' && sortOrder !== 'manual') {
      const visibleIds = visibleDevices.map((device) => device.deviceId);
      setManualOrder((current) => [
        ...visibleIds,
        ...current.filter((deviceId) => !visibleIds.includes(deviceId)),
      ]);
    }
    setSortOrder(nextSortOrder);
  };

  const moveManualDevice = (targetDeviceId: string) => {
    if (!draggingDeviceId || draggingDeviceId === targetDeviceId) return;
    if (lastDragTargetRef.current === targetDeviceId) return;
    lastDragTargetRef.current = targetDeviceId;
    setManualOrder((current) => {
      const sourceIndex = current.indexOf(draggingDeviceId);
      const targetIndex = current.indexOf(targetDeviceId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const next = [...current];
      next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, draggingDeviceId);
      return next;
    });
  };

  return (
    <div className="device-monitor-page">
      <header className="device-monitor-header">
        <div>
          <h1>デバイスモニター</h1>
          <p>全デバイスの最新値とトレンドグラフを一覧表示します。</p>
        </div>
        <div className="device-monitor-headerActions">
          <span>{visibleDevices.length} / {devices.length}台</span>
          <button
            type="button"
            className={autoRefresh ? 'is-enabled' : ''}
            onClick={() => setAutoRefresh((current) => !current)}>
            自動更新 {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            type="button"
            className={showKpi ? 'is-enabled' : ''}
            aria-pressed={showKpi}
            onClick={() => setShowKpi((current) => !current)}>
            KPI {showKpi ? '表示' : '非表示'}
          </button>
          <button type="button" onClick={() => void loadDevices()} aria-label="再読み込み">
            <AiOutlineReload className={isLoading ? 'device-spin' : ''} size={20} />
          </button>
          <Link to="/map">地図へ戻る</Link>
        </div>
      </header>

      <div className="device-monitor-filters">
        <input
          type="search"
          placeholder="デバイス名・ID・河川名で検索"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <select value={areaFilter} onChange={(event) => setAreaFilter(event.target.value)}>
          <option value="">すべての地域</option>
          {areas.map((area) => (
            <option key={area} value={area}>{area}</option>
          ))}
        </select>
        <label className="device-monitor-sort">
          <span>並び順</span>
          <select value={sortOrder} onChange={(event) => selectSortOrder(event.target.value as DeviceSortOrder)}>
            <option value="area-name">地域 → デバイス名</option>
            <option value="name-asc">デバイス名（昇順）</option>
            <option value="name-desc">デバイス名（降順）</option>
            <option value="updated-desc">更新日時（新しい順）</option>
            <option value="updated-asc">更新日時（古い順）</option>
            <option value="active-first">稼働中を優先</option>
            <option value="manual">手動（ドラッグ）</option>
          </select>
        </label>
      </div>

      {sortOrder === 'manual' && (
        <p className="device-monitor-sortHelp">カード上部のハンドルをドラッグして並び替えできます。順番は自動保存されます。</p>
      )}

      {isLoading && !devices.length ? (
        <div className="device-monitor-message">デバイスを読み込んでいます...</div>
      ) : visibleDevices.length ? (
        <main className="device-monitor-grid">
          {visibleDevices.map((device) => (
            <div
              key={device.deviceId}
              className={`device-monitor-item ${draggingDeviceId === device.deviceId ? 'is-dragging' : ''}`}
              onDragOver={(event) => {
                if (sortOrder !== 'manual') return;
                event.preventDefault();
              }}
              onDragEnter={() => moveManualDevice(device.deviceId)}
              onDrop={(event) => event.preventDefault()}>
              {sortOrder === 'manual' && (
                <div
                  className="device-monitor-dragHandle"
                  draggable
                  onDragStart={(event) => {
                    setDraggingDeviceId(device.deviceId);
                    lastDragTargetRef.current = '';
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', device.deviceId);
                  }}
                  onDragEnd={() => {
                    setDraggingDeviceId('');
                    lastDragTargetRef.current = '';
                  }}>
                  <span aria-hidden="true">⋮⋮</span>
                  ドラッグして並び替え
                </div>
              )}
              <DeviceDetailPanel dataDialog={device} layoutMode="embedded" showKpi={showKpi} />
            </div>
          ))}
        </main>
      ) : (
        <div className="device-monitor-message">条件に一致するデバイスがありません。</div>
      )}
    </div>
  );
}
